import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/toast';
import BarcodeScanner from '../components/qr/BarcodeScanner';
import VisitorInfoModal from '../components/qr/VisitorInfoModal';
import { RegistrationStats } from '../types';
import { getDashboardStatistics, getDailyStatistics, getRegistrationByQR, markAsEntered, RegistrationData, TicketType, getExhibitionCounts } from '../lib/api';

/**
 * 관리자 대시보드 페이지
 * 전시회 등록 현황과 통계를 모니터링하는 페이지입니다.
 */
const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<RegistrationStats | null>(null);
  const [dailyStats, setDailyStats] = useState<any[]>([]);
  const [exhibitionCounts, setExhibitionCounts] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [isVisitorInfoModalOpen, setIsVisitorInfoModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<RegistrationData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [viewMode, setViewMode] = useState<'overall' | 'daily'>('overall');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const { showToast } = useToast();

  // 통계 데이터 로드
  const loadStatistics = useCallback(async () => {
    try {
      setIsLoading(true);
      const [dashboardData, exhibitionData] = await Promise.all([
        getDashboardStatistics(),
        getExhibitionCounts()
      ]);
      setStats(dashboardData);
      setExhibitionCounts(exhibitionData);
    } catch (error) {
      console.error('통계 데이터 로드 실패:', error);
      if (error instanceof Error) {
        if (error.message.includes('relation "public.registrations" does not exist')) {
          showToast('⚠️ 데이터베이스 테이블이 생성되지 않았습니다. 관리자에게 문의하세요.', 'error');
        } else {
          showToast('통계 데이터를 불러오는데 실패했습니다.', 'error');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  // 일별 통계 데이터 로드
  const loadDailyStatistics = useCallback(async () => {
    try {
      const data = await getDailyStatistics(dateRange.startDate, dateRange.endDate);
      setDailyStats(data);
    } catch (error) {
      console.error('일별 통계 로드 실패:', error);
      showToast('일별 통계 데이터를 불러오는데 실패했습니다.', 'error');
    }
  }, [dateRange.startDate, dateRange.endDate, showToast]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // 일별 통계 로드
  useEffect(() => {
    if (viewMode === 'daily') {
      loadDailyStatistics();
    }
  }, [viewMode, loadDailyStatistics]);

  // QR코드 스캔 결과 처리
  const handleQRScanResult = async (qrCode: string) => {
    try {
      // QR코드로 등록자 정보 조회
      const registration = await getRegistrationByQR(qrCode);
      
      if (!registration) {
        showToast('해당 QR코드에 대한 등록자 정보를 찾을 수 없습니다.', 'error');
        return;
      }

      // 등록자 정보를 설정하고 팝업 열기
      setSelectedRegistration(registration);
      setIsVisitorInfoModalOpen(true);
      setIsQRScannerOpen(false);
      
    } catch (error) {
      console.error('QR코드 처리 오류:', error);
      showToast('QR코드 처리 중 오류가 발생했습니다.', 'error');
    }
  };

  // 입장 처리 핸들러
  const handleMarkAsEntered = async (registrationData: RegistrationData, ticketType: TicketType) => {
    try {
      setIsProcessing(true);
      
      // 이미 입장한 경우 체크
      if (registrationData.is_entered) {
        showToast(`${registrationData.name}님은 이미 입장 완료된 상태입니다.`, 'info');
        return;
      }

      // 입장 처리 (registration_id 사용 - 더 안전하고 직관적)
      const idToUse = registrationData.registration_id || registrationData.id || '';
      console.log('🎫 [AdminDashboard] 사용할 ID:', idToUse, 'registration_id:', registrationData.registration_id, 'uuid:', registrationData.id);
      await markAsEntered(idToUse, ticketType);
      showToast(`${registrationData.name}님이 ${ticketType} 티켓으로 입장 처리되었습니다.`, 'success');
      
      // 통계 새로고침
      loadStatistics();
      
      // 모달 닫기
      setIsVisitorInfoModalOpen(false);
      setSelectedRegistration(null);
      
    } catch (error) {
      console.error('입장 처리 오류:', error);
      showToast('입장 처리 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 관람객 정보 모달 닫기
  const handleCloseVisitorInfoModal = () => {
    setIsVisitorInfoModalOpen(false);
    setSelectedRegistration(null);
  };

  // 로딩 상태 표시
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl px-4 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">통계 데이터를 불러오는 중...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // 데이터가 없을 때 표시
  if (!stats) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">데이터를 불러올 수 없습니다</h2>
            <p className="text-gray-600 mb-6">데이터베이스 연결을 확인하거나 관리자에게 문의하세요.</p>
            <Button onClick={loadStatistics}>다시 시도</Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* 페이지 제목 및 네비게이션 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                관리자 대시보드
              </h1>
              <p className="text-gray-600">
                전시회 등록 현황과 통계를 실시간으로 모니터링하세요.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                onClick={() => setIsQRScannerOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                📷 QR코드 스캔
              </Button>
              <Link to="/admin/registrations">
                <Button variant="outline">
                  등록자 목록 관리
                </Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={() => {
                  showToast('데이터를 새로고침하고 있습니다...', 'info');
                  loadStatistics();
                }}
              >
                실시간 새로고침
              </Button>
            </div>
          </div>
        </div>

        {/* 네비게이션 탭 */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <div className="border-b-2 border-blue-500 py-2 px-1">
              <div className="flex items-center">
                <span className="text-blue-600 font-medium">📊 대시보드</span>
              </div>
            </div>
            <Link to="/admin/registrations">
              <div className="border-b-2 border-transparent py-2 px-1 hover:border-gray-300">
                <div className="flex items-center text-gray-500 hover:text-gray-700">
                  <span>📋 등록자 목록</span>
                </div>
              </div>
            </Link>
          </nav>
        </div>

        {/* 뷰 모드 선택 및 날짜 필터 */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'overall' ? 'default' : 'outline'}
                onClick={() => setViewMode('overall')}
                size="sm"
              >
                전체 현황
              </Button>
              <Button
                variant={viewMode === 'daily' ? 'default' : 'outline'}
                onClick={() => setViewMode('daily')}
                size="sm"
              >
                일별 현황
              </Button>
            </div>
            
            {viewMode === 'daily' && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">기간:</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded text-sm"
                />
                <span className="text-gray-500">~</span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded text-sm"
                />
                <Button size="sm" onClick={loadDailyStatistics}>
                  조회
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* 실제 등록 현황 표시 */}
        {exhibitionCounts && (
          <div className="mb-6">
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">실제 등록 현황</h3>
                    <p className="text-sm text-gray-600">
                      전체 등록: {exhibitionCounts.total.toLocaleString()}명 | 입장 완료: {exhibitionCounts.entered.toLocaleString()}명
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">{exhibitionCounts.total.toLocaleString()}</div>
                    <div className="text-sm text-green-600">입장: {exhibitionCounts.entered.toLocaleString()}명</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>전시회별 분포</span>
                    <span>입장률: {exhibitionCounts.total > 0 ? Math.round((exhibitionCounts.entered / exhibitionCounts.total) * 100) : 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full"
                      style={{ 
                        width: `${exhibitionCounts.total > 0 ? (exhibitionCounts.entered / exhibitionCounts.total) * 100 : 0}%` 
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-purple-600">UrbanBreak:</span>
                      <span className="font-medium">{exhibitionCounts.urbanbreak.toLocaleString()}명</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-red-600">ToyConvention:</span>
                      <span className="font-medium">{exhibitionCounts.toycon.toLocaleString()}명</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* 통계 카드 */}
        {viewMode === 'overall' && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500">
                총 등록자 수
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {exhibitionCounts ? exhibitionCounts.total.toLocaleString() : stats.total_registrations.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                전체 사전등록 완료
              </p>
              {exhibitionCounts && (
                <div className="mt-2">
                  <p className="text-xs text-purple-600">UrbanBreak: {exhibitionCounts.urbanbreak.toLocaleString()}명</p>
                  <p className="text-xs text-red-600">ToyConvention: {exhibitionCounts.toycon.toLocaleString()}명</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500">
                입장 완료자 수
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {exhibitionCounts ? exhibitionCounts.entered.toLocaleString() : stats.total_entries.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                입장률 {exhibitionCounts ? Math.round((exhibitionCounts.entered / exhibitionCounts.total) * 100) : (stats.total_registrations > 0 ? Math.round((stats.total_entries / stats.total_registrations) * 100) : 0)}%
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500">
                금일 등록자 수
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {stats.today_registrations.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                오늘 신규 등록
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-500">
                대기 중인 등록자
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {exhibitionCounts ? (exhibitionCounts.total - exhibitionCounts.entered).toLocaleString() : stats.pending_registrations.toLocaleString()}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                입장 대기 중
              </p>
            </CardContent>
          </Card>
        </div>

        {/* 상세 통계 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 성별 분포 */}
          <Card>
            <CardHeader>
              <CardTitle>성별 분포</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">남성</span>
                  <span className="text-sm font-medium">
                    {(stats.gender_stats.male || 0).toLocaleString()}명
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ 
                      width: `${stats.total_registrations > 0 ? ((stats.gender_stats.male || 0) / stats.total_registrations) * 100 : 0}%` 
                    }}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">여성</span>
                  <span className="text-sm font-medium">
                    {(stats.gender_stats.female || 0).toLocaleString()}명
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-pink-600 h-2 rounded-full"
                    style={{ 
                      width: `${stats.total_registrations > 0 ? ((stats.gender_stats.female || 0) / stats.total_registrations) * 100 : 0}%` 
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 연령대별 분포 */}
          <Card>
            <CardHeader>
              <CardTitle>연령대별 분포</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats.age_group_stats).map(([age, count]) => {
                  const ageLabels = {
                    teen: '10대',
                    twenties: '20대',
                    thirties: '30대',
                    forties: '40대',
                    fifties: '50대',
                    sixties_plus: '60대 이상'
                  };
                  
                  return (
                    <div key={age} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {ageLabels[age as keyof typeof ageLabels] || age}
                      </span>
                      <span className="text-sm font-medium">
                        {(count as number).toLocaleString()}명
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* 지역별 분포 */}
          <Card>
            <CardHeader>
              <CardTitle>지역별 분포</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats.region_stats).map(([region, count]) => {
                  const regionLabels = {
                    seoul: '서울',
                    busan: '부산',
                    daegu: '대구',
                    incheon: '인천',
                    gwangju: '광주',
                    daejeon: '대전',
                    ulsan: '울산',
                    sejong: '세종',
                    gyeonggi: '경기',
                    gangwon: '강원',
                    chungbuk: '충북',
                    chungnam: '충남',
                    jeonbuk: '전북',
                    jeonnam: '전남',
                    gyeongbuk: '경북',
                    gyeongnam: '경남',
                    jeju: '제주',
                    other: '기타'
                  };
                  
                  return (
                    <div key={region} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {regionLabels[region as keyof typeof regionLabels] || region}
                      </span>
                      <span className="text-sm font-medium">
                        {(count as number).toLocaleString()}명
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
        </>
        )}

        {viewMode === 'daily' && (
        /* 일별 통계 뷰 */
        <div className="space-y-6 mb-8">
          {dailyStats.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📅</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">선택한 기간에 데이터가 없습니다</h3>
                  <p className="text-gray-500">다른 날짜 범위를 선택해보세요.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                일별 등록 현황 ({dateRange.startDate} ~ {dateRange.endDate})
              </h3>
              {dailyStats.map((dayData) => (
                <Card key={dayData.date}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900">
                        {new Date(dayData.date).toLocaleDateString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          weekday: 'short'
                        })}
                      </h4>
                      <div className="text-sm text-gray-500">
                        총 {dayData.total_registrations}명 등록
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">
                          {dayData.total_registrations}
                        </div>
                        <div className="text-xs text-gray-500">총 등록</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">
                          {dayData.total_entries}
                        </div>
                        <div className="text-xs text-gray-500">입장 완료</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-lg font-bold text-purple-600">
                          {dayData.urbanbreak}
                        </div>
                        <div className="text-xs text-gray-500">UrbanBreak</div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-lg font-bold text-red-600">
                          {dayData.toycon}
                        </div>
                        <div className="text-xs text-gray-500">ToyConvention</div>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <div className="flex gap-4">
                        <span className="text-blue-600">남성: {dayData.gender_stats.male}</span>
                        <span className="text-pink-600">여성: {dayData.gender_stats.female}</span>
                        <span className="text-gray-600">기타: {dayData.gender_stats.other}</span>
                      </div>
                      <div className="text-gray-500">
                        입장률: {dayData.total_registrations > 0 ? Math.round((dayData.total_entries / dayData.total_registrations) * 100) : 0}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* 일별 통계 요약 */}
              <Card className="bg-gray-50">
                <CardContent className="pt-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">기간별 요약</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-xl font-bold text-blue-600">
                        {dailyStats.reduce((sum, day) => sum + day.total_registrations, 0)}
                      </div>
                      <div className="text-sm text-gray-600">총 등록자</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-green-600">
                        {dailyStats.reduce((sum, day) => sum + day.total_entries, 0)}
                      </div>
                      <div className="text-sm text-gray-600">총 입장자</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-purple-600">
                        {dailyStats.reduce((sum, day) => sum + day.urbanbreak, 0)}
                      </div>
                      <div className="text-sm text-gray-600">UrbanBreak</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-red-600">
                        {dailyStats.reduce((sum, day) => sum + day.toycon, 0)}
                      </div>
                      <div className="text-sm text-gray-600">ToyConvention</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
        )}

        {/* 퀘이크 액세스 카드 */}
        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>빠른 액세스</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link to="/admin/registrations">
                  <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">📋</div>
                      <div>
                        <h3 className="font-medium text-gray-900">등록자 목록 관리</h3>
                        <p className="text-sm text-gray-600">등록자 검색, 필터링, 입장 상태 관리</p>
                      </div>
                    </div>
                  </div>
                </Link>

                <Link to="/admin/registrations?filter=pending">
                  <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">⏳</div>
                      <div>
                        <h3 className="font-medium text-gray-900">대기 중인 등록자</h3>
                        <p className="text-sm text-gray-600">입장 대기 중인 {exhibitionCounts ? (exhibitionCounts.total - exhibitionCounts.entered).toLocaleString() : stats.pending_registrations}명 확인</p>
                      </div>
                    </div>
                  </div>
                </Link>

                <button
                  onClick={() => showToast('데이터 내보내기 기능은 등록자 목록 페이지에서 이용하실 수 있습니다.', 'info')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">📤</div>
                    <div>
                      <h3 className="font-medium text-gray-900">데이터 내보내기</h3>
                      <p className="text-sm text-gray-600">전체 등록자 데이터 CSV 다운로드</p>
                    </div>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 바코드 스캐너 모달 */}
        <BarcodeScanner
          isOpen={isQRScannerOpen}
          onClose={() => setIsQRScannerOpen(false)}
          onResult={handleQRScanResult}
        />

        {/* 관람객 정보 모달 */}
        <VisitorInfoModal
          isOpen={isVisitorInfoModalOpen}
          onClose={handleCloseVisitorInfoModal}
          registrationData={selectedRegistration}
          onMarkAsEntered={handleMarkAsEntered}
          isProcessing={isProcessing}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 